import { Separator } from "@/components/ui/separator";
import type { CotacaoEcommerce } from "@/types/Cotacao";
import { formatMoneyBr } from "@/utils/format";
import { computeQuoteTotals } from "@/utils/pricing";

interface FreightDetailsCardProps {
  cotacao: CotacaoEcommerce;
}

function formatAddress(parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(", ");
}

export function FreightDetailsCard({ cotacao }: FreightDetailsCardProps) {
  const price = computeQuoteTotals(cotacao);
  const hasColeta = Number(cotacao.tro_codigo) > 1;
  const hasEntrega = Number(cotacao.trd_codigo) > 1;

  const coletaAddress = formatAddress([
    cotacao.cve_coleta_endereco,
    cotacao.cve_coleta_numero,
    cotacao.cve_coleta_complemento || undefined,
    cotacao.cve_coleta_bairro,
    `CEP: ${cotacao.cve_coleta_cep}`,
    cotacao.mun_descricao_coleta,
  ]);

  const entregaAddress = formatAddress([
    cotacao.cve_entrega_endereco,
    cotacao.cve_entrega_numero,
    cotacao.cve_entrega_complemento || undefined,
    cotacao.cve_entrega_bairro,
    `CEP: ${cotacao.cve_entrega_cep}`,
    cotacao.mun_descricao_entrega,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-n-green">Detalhes do frete:</h3>

      <div className="space-y-6 ">
        <div className="grid grid-cols-2  md:gap-x-6 gap-x-2 md:gap-y-4  gap-y-2">
          <div>
            <p className="text-sm font-medium text-n-green">
              Cidade de origem:
            </p>
            <p className="text-sm font-normal text-gray-700">
              {cotacao.mun_descricao_origem}/{cotacao.bop_origem_descricao}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-n-green">
              {hasColeta ? "Local de Coleta:" : "Local de Recebimento:"}
            </p>
            <p className="text-sm font-normal text-gray-700">
              {hasColeta
                ? coletaAddress || "—"
                : `${cotacao.municipio_base_origem}/${cotacao.bop_origem_descricao}`}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-n-green">
              Cidade de destino:
            </p>
            <p className="text-sm font-normal text-gray-700">
              {cotacao.mun_descricao_destino}/{cotacao.bop_destino_descricao}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-n-green">
              {hasEntrega ? "Local de Entrega:" : "Local de Retirada:"}
            </p>
            <p className="text-sm font-normal text-gray-700">
              {hasEntrega
                ? entregaAddress || "—"
                : `${cotacao.municipio_base_destino}/${cotacao.bop_destino_descricao}`}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-sm font-medium text-n-green">
              Rota do transporte:
            </p>
            <p className="text-sm font-normal text-gray-700">
              {cotacao.cve_rota_texto || "—"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3  md:gap-x-6 md:gap-y-4 gap-x-4 gap-y-2">
          <div>
            <p className="text-sm font-medium text-n-green">Prazo entrega:</p>
            <p className="text-sm font-normal text-gray-700">
              {cotacao.cve_prazo_entrega
                ? `${cotacao.cve_prazo_entrega} dias úteis`
                : "—"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-n-green">
              Vencimento do prazo:
            </p>
            <p className="text-sm font-normal text-gray-700">
              {cotacao.cve_data_prazo_entrega || "—"}
            </p>
          </div>

          <div>
            <p className="text-sm font-bold text-n-green">Valor:</p>
            <p className="text-sm font-bold text-gray-700">
              {formatMoneyBr(price.total) || "—"}
            </p>
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-n-green">
            Informações do Veículo:
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-4">
            <div>
              <p className="text-sm font-medium text-n-green">Veículo:</p>
              <p className="text-sm font-normal text-gray-700">
                {cotacao.vmd_descricao || "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-n-green">Placa:</p>
              <p className="text-sm font-normal text-gray-700">
                {cotacao.cve_placa || "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-n-green">Ano/modelo:</p>
              <p className="text-sm font-normal text-gray-700">
                {cotacao.cve_ano_fab && cotacao.cve_ano_mod
                  ? `${cotacao.cve_ano_fab}/${cotacao.cve_ano_mod}`
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-n-green">
                Valor do veículo:
              </p>
              <p className="text-sm font-normal text-gray-700">
                {formatMoneyBr(cotacao.cve_valor_veiculo) || "—"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-n-green">Seguro:</p>
              <p className="text-sm font-normal text-gray-700">
                {cotacao.cve_tipo_seguro === "0"
                  ? "Básico"
                  : cotacao.cve_tipo_seguro === "2"
                    ? "Completo"
                    : "—"}
              </p>
            </div>
          </div>
        </div>

        {/* <Separator /> */}

        {/* <div className="space-y-3">
          <h4 className="text-base font-semibold text-n-green">
            Resumo Financeiro:
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <p className="text-sm text-gray-700">Valor base do frete:</p>
              <p className="text-sm font-medium text-gray-900">
                {formatMoneyBr(price.base)}
              </p>
            </div>
            {price.additionals > 0 && (
              <div className="flex justify-between">
                <p className="text-sm text-gray-700">Adicionais:</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatMoneyBr(price.additionals)}
                </p>
              </div>
            )}
            {price.discount > 0 && (
              <div className="flex justify-between text-n-text-red">
                <p className="text-sm">Desconto:</p>
                <p className="text-sm font-medium">
                  - {formatMoneyBr(price.discount)}
                </p>
              </div>
            )}

            <div className="flex justify-between border-t border-dashed border-border pt-2">
              <p className="text-base font-semibold text-n-green">
                Valor total:
              </p>

              <p className="text-base font-bold text-n-green">
                {formatMoneyBr(price.total)}
              </p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}
